import React from 'react'
import Joi from 'joi'
import { t } from 'i18next'
import { Dialog } from '@mui/material'
import {
  Close as CloseIcon, Telegram as TelegramIcon, ImageOutlined, DeleteOutline, OpenInNew, SendRounded, BlockRounded, PercentRounded,
} from '@mui/icons-material'
import { useDispatch } from '../../../store'
import { postTelegramCampaign } from '../../../store/marketing/actionCreator'
import { clearFieldError } from '../../../store/common/actionCreator'
import { ErrorsState, validateForm } from '../../../function/validation'
import { TableRef } from '../../../component/datatable/types'
import { ITelegramCampaign } from '../../../store/marketing/reducer'
import { formatDate } from '../../../function/date'
import l from '../../../page/SubscribersPageV2.module.css'
import s from '../campaign/CampaignDialogV2.module.css'
import tg from './TelegramDialogV2.module.css'

/*
 * A new Telegram campaign in the v2 admin design — the classic FormTelegram's
 * flow unchanged (the same Joi rules, the same JPG/PNG ≤ 4 MB picture, the
 * same POST through postTelegramCampaign as multipart form data). New on top:
 * a live preview of the message as Telegram shows it. The classic marketing
 * page keeps TelegramCreate / FormTelegram.
 *
 * TelegramCampaignViewV2 (below) is a sent campaign's properties in the same
 * design — PopupTgPreview's facts, plus the message as it was sent.
 */

const NAME_MAX = 55
const TITLE_MAX = 255
const PARAGRAPH_MAX = 500
const ACTION_MAX = 25
const IMAGE_MAX = 4 * 1024 * 1024
const IMAGE_TYPES = ['jpg', 'jpeg', 'png']

// FormTelegram's rules, word for word
const schema = Joi.object({
  name: Joi.string().max(NAME_MAX).label(t('field.label.campaignName')),
  title: Joi.string().max(TITLE_MAX).label(t('field.label.mainTitle')),
  paragraph: Joi.string().max(PARAGRAPH_MAX).label(t('field.label.paragraph')),
  actionName: Joi.string().max(ACTION_MAX).label(t('field.label.callToActionName')),
  actionUrl: Joi.string().uri({ allowQuerySquareBrackets: true }).label(t('field.label.callToActionUrl')),
})

function Field({ label, error, count, max, children }: {
  label: string; error?: string; count?: number; max?: number; children: React.ReactNode
}) {
  return (
    <label className={s.field}>
      <span className={s.labelRow}>
        <span className={s.label}>{label}</span>
        {max != null && <span className={`${s.count} ${(count || 0) > max ? s.over : ''}`}>{count || 0} / {max}</span>}
      </span>
      {children}
      {error && <span className={s.err}>{error}</span>}
    </label>
  )
}

export function TelegramDialogV2({ theme, tableRef, onClose }: {
  theme: 'light' | 'dark'
  tableRef: React.RefObject<TableRef>
  onClose: () => void
}) {
  const dispatch = useDispatch()
  const fileRef = React.useRef<HTMLInputElement>(null)
  const [name, setName] = React.useState('')
  const [title, setTitle] = React.useState('')
  const [paragraph, setParagraph] = React.useState('')
  const [actionName, setActionName] = React.useState('')
  const [actionUrl, setActionUrl] = React.useState('')
  const [image, setImage] = React.useState<File>()
  const [imageUrl, setImageUrl] = React.useState<string>()
  const [errors, setErrors] = React.useState<ErrorsState>(null)

  React.useEffect(() => { setErrors(null); dispatch(clearFieldError) }, [dispatch, name, title, paragraph, actionName, actionUrl, image])
  React.useEffect(() => () => { if (imageUrl) URL.revokeObjectURL(imageUrl) }, [imageUrl])

  const err = (k: string) => {
    const v = errors?.[k]
    return typeof v === 'string' ? v : undefined
  }

  function pick(file?: File) {
    if (!file) return
    const ext = (file.name.split('.').pop() || '').toLowerCase()
    if (file.size > IMAGE_MAX) { setErrors({ image: 'Photo size should be less than 4 Mb.' }); return }
    if (!IMAGE_TYPES.includes(ext)) { setErrors({ image: t('message.error.profilePicture') }); return }
    setImage(file)
    setImageUrl(URL.createObjectURL(file))
  }

  function removeImage() {
    if (fileRef.current) fileRef.current.value = ''
    setImage(undefined)
    setImageUrl(undefined)
  }

  function submit() {
    const error = validateForm(schema, {
      name: name.trim(), title: title.trim(), paragraph: paragraph.trim(), actionName: actionName.trim(), actionUrl,
    })
    if (error) { setErrors(error); return }
    const form = new FormData()
    form.append('name', name)
    form.append('title', title)
    form.append('paragraph', paragraph)
    form.append('actionName', actionName)
    form.append('actionUrl', actionUrl)
    if (image) form.append('file', image)
    dispatch(postTelegramCampaign(form, () => tableRef.current?.refresh()))
    onClose()
  }

  return (
    <Dialog open onClose={onClose} maxWidth={false} PaperProps={{ sx: { background: 'transparent', boxShadow: 'none', m: 2, maxHeight: 'calc(100vh - 32px)' } }}>
      <div className={`${l.theme} ${s.shell} ${tg.shell}`} data-theme={theme}>
        <div className={s.top}>
          <span className={`${s.icon} ${tg.icon}`}><TelegramIcon sx={{ fontSize: 19 }} /></span>
          <div className={s.topText}>
            <b>New Telegram campaign</b>
            <span>A message to the bot&apos;s subscribers — the preview on the right is what they will see.</span>
          </div>
          <button type="button" className={s.close} onClick={onClose} aria-label="Close"><CloseIcon sx={{ fontSize: 18 }} /></button>
        </div>

        <div className={`${s.main} ${tg.main}`}>
          <div className={s.form}>
            <div className={tg.section}>
              <Field label="Campaign name — for you, not sent" error={err('name')} count={name.length} max={NAME_MAX}>
                <input className={s.input} value={name} onChange={(e) => setName(e.target.value)} placeholder="Black Friday 2026" />
              </Field>
              <Field label="Main title" error={err('title')} count={title.length} max={TITLE_MAX}>
                <input className={s.input} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="The bold first line of the message" />
              </Field>
              <Field label="Paragraph" error={err('paragraph')} count={paragraph.length} max={PARAGRAPH_MAX}>
                <textarea className={`${s.input} ${s.textarea}`} rows={6} value={paragraph} onChange={(e) => setParagraph(e.target.value)} placeholder="Enter campaign text" />
              </Field>
            </div>

            <div className={tg.section}>
              <span className={s.label}>Image <em className={tg.optional}>optional · JPG or PNG, up to 4 MB</em></span>
              {imageUrl ? (
                <div className={tg.imageRow}>
                  <img src={imageUrl} alt="" className={tg.thumb} />
                  <span className={tg.imageName}>{image?.name}</span>
                  <button type="button" className={s.ghost} onClick={removeImage}><DeleteOutline sx={{ fontSize: 16 }} />Remove</button>
                </div>
              ) : (
                <button type="button" className={tg.drop} onClick={() => fileRef.current?.click()}>
                  <ImageOutlined sx={{ fontSize: 20 }} />Upload image
                </button>
              )}
              <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,image/jpeg,image/png" hidden onChange={(e) => pick(e.target.files?.[0])} />
              {err('image') && <span className={s.err}>{err('image')}</span>}
            </div>

            <div className={tg.section}>
              <div className={s.grid2}>
                <Field label="Button text" error={err('actionName')} count={actionName.length} max={ACTION_MAX}>
                  <input className={s.input} value={actionName} onChange={(e) => setActionName(e.target.value)} placeholder="Get 20% off" />
                </Field>
                <Field label="Button link" error={err('actionUrl')}>
                  <span className={s.linkRow}>
                    <input className={s.input} value={actionUrl} onChange={(e) => setActionUrl(e.target.value)} placeholder="https://…" />
                    <button
                      type="button"
                      className={s.testBtn}
                      disabled={!/^https?:\/\/\S+\.\S+/i.test(actionUrl.trim())}
                      onClick={(e) => { e.preventDefault(); window.open(actionUrl.trim(), '_blank', 'noopener,noreferrer') }}
                      title="Open the link in a new tab"
                    >
                      <OpenInNew sx={{ fontSize: 15 }} />Test
                    </button>
                  </span>
                </Field>
              </div>
            </div>
          </div>

          {/* the message as Telegram shows it */}
          <div className={s.preview}>
            <div className={s.previewHead}>In Telegram</div>
            <TelegramMessage image={imageUrl} title={title.trim() || 'Main title'} text={paragraph.trim() || 'The campaign text goes here.'} button={actionName.trim() || 'Button'} time="now" />
          </div>
        </div>

        <div className={s.foot}>
          {errors ? <span className={s.footErr}>Some fields need attention</span> : (
            <span className={s.footNote}>Sent as soon as it is started</span>
          )}
          <button type="button" className={s.ghost} onClick={onClose}>Cancel</button>
          <button type="button" className={s.primary} onClick={submit}>Start campaign</button>
        </div>
      </div>
    </Dialog>
  )
}

/** the message as Telegram shows it: the picture, the bold title, the text, the time — and its button under it */
function TelegramMessage({ image, title, text, button, time, href }: {
  image?: string; title: string; text: string; button?: string; time: string; href?: string
}) {
  return (
    <div className={tg.chat}>
      <div className={tg.bubble}>
        {image ? <img src={image} alt="" className={tg.photo} /> : null}
        <div className={tg.text}>
          <b>{title}</b>
          <span>{text}</span>
          <i className={tg.time}>{time}</i>
        </div>
      </div>
      {button && (
        href
          ? <a className={tg.button} href={href} target="_blank" rel="noopener noreferrer">{button}<OpenInNew sx={{ fontSize: 12 }} /></a>
          : <div className={tg.button}>{button}<OpenInNew sx={{ fontSize: 12 }} /></div>
      )}
    </div>
  )
}

/**
 * A sent Telegram campaign — PopupTgPreview's facts (who got it, who has
 * blocked the bot, when, the link) in the v2 design, and the message itself
 * as the subscribers saw it.
 */
export function TelegramCampaignViewV2({ data, theme, onClose }: {
  data: ITelegramCampaign
  theme: 'light' | 'dark'
  onClose: () => void
}) {
  const sent = Number(data?.results?.successfulSent) || 0
  const blocked = Number(data?.results?.failedSent) || 0
  const total = sent + blocked
  const rate = total ? Math.round((sent / total) * 100) : null
  const created = data.createdTime ? formatDate(data.createdTime, 'dd.MM.yyyy HH:mm') : '—'

  return (
    <Dialog open onClose={onClose} maxWidth={false} PaperProps={{ sx: { background: 'transparent', boxShadow: 'none', m: 2, maxHeight: 'calc(100vh - 32px)' } }}>
      <div className={`${l.theme} ${s.shell} ${tg.shell}`} data-theme={theme}>
        <div className={s.top}>
          <span className={`${s.icon} ${tg.icon}`}><TelegramIcon sx={{ fontSize: 19 }} /></span>
          <div className={s.topText}>
            <b>{data.name || 'Telegram campaign'}</b>
            <span>Sent {created}</span>
          </div>
          <button type="button" className={s.close} onClick={onClose} aria-label="Close"><CloseIcon sx={{ fontSize: 18 }} /></button>
        </div>

        <div className={`${s.main} ${tg.main}`}>
          <div className={s.form}>
            <div className={tg.section}>
              <div className={tg.stats}>
                <div className={tg.stat}><span className={tg.statIcon}><SendRounded sx={{ fontSize: 16 }} /></span><small>Delivered</small><b>{sent.toLocaleString()}</b></div>
                <div className={`${tg.stat} ${blocked ? tg.statBad : ''}`}><span className={tg.statIcon}><BlockRounded sx={{ fontSize: 16 }} /></span><small>Blocked the bot</small><b>{blocked.toLocaleString()}</b></div>
                <div className={tg.stat}><span className={tg.statIcon}><PercentRounded sx={{ fontSize: 16 }} /></span><small>Delivery rate</small><b>{rate === null ? '—' : `${rate}%`}</b></div>
              </div>
            </div>
            <div className={tg.section}>
              <dl className={tg.facts}>
                <dt>Campaign name</dt><dd>{data.name || '—'}</dd>
                <dt>Main title</dt><dd>{data.title || '—'}</dd>
                <dt>Created</dt><dd>{created}</dd>
                <dt>Button text</dt><dd>{data.actionName || '—'}</dd>
                <dt>Button link</dt>
                <dd>{data.actionUrl ? <a href={data.actionUrl} target="_blank" rel="noopener noreferrer" className={tg.link}>{data.actionUrl}<OpenInNew sx={{ fontSize: 13 }} /></a> : '—'}</dd>
              </dl>
            </div>
          </div>

          <div className={s.preview}>
            <div className={s.previewHead}>As sent in Telegram</div>
            <TelegramMessage
              image={data.image || undefined}
              title={data.title || '—'}
              text={data.paragraph || ''}
              button={data.actionName || undefined}
              href={data.actionUrl || undefined}
              time={data.createdTime ? formatDate(data.createdTime, 'HH:mm') : ''}
            />
          </div>
        </div>

        <div className={s.foot}>
          <span className={s.footNote}>A campaign is sent once — it cannot be changed or sent again</span>
          <button type="button" className={s.ghost} onClick={onClose}>Close</button>
        </div>
      </div>
    </Dialog>
  )
}
